/**
 * AI 검토 — 본문 HTML 의 구조를 보존하면서 "텍스트만" AI 가 다듬게 하기 위한 헬퍼 모음.
 *
 * <h3>왜 필요한가</h3>
 * <p>이전엔 Tiptap 의 {@code editor.getText()} 결과 평문을 통째로 AI 에게 보내고
 * 받은 평문을 본문에 통째로 덮어썼다. 그 결과 이미지·표·hr 같은 구조가 모두 사라졌다.
 *
 * <h3>새 전략 — 번호 매긴 텍스트 블록 교환</h3>
 * <ol>
 *   <li>{@link extractBlocks}: bodyHtml 을 DOM 으로 파싱해서 텍스트가 있는
 *       "안전한" 블록(p / h1~h6 / li / td / th / blockquote 중 img/table 자식 없는 것)만 수집</li>
 *   <li>{@link toNumbered}: 그 블록들의 텍스트를 [1] [2] ... 번호 매겨 한 문자열로 직렬화</li>
 *   <li>이 문자열을 AI 에 보냄 → AI 도 동일한 [N] 형식으로 다듬은 텍스트 반환</li>
 *   <li>{@link parseNumbered}: AI 출력을 [N] 단위로 다시 파싱해 Map 반환</li>
 *   <li>{@link applyRevisions}: 처음 추출한 블록들의 textContent 만 차례로 교체하고
 *       전체 doc 의 innerHTML 을 직렬화해 반환 — 이미지·표·링크 attr 모두 그대로 유지</li>
 * </ol>
 */

const SAFE_TAGS = new Set(["P", "H1", "H2", "H3", "H4", "H5", "H6", "LI", "TD", "TH", "BLOCKQUOTE"]);
const STRUCTURAL_DESCENDANTS = "img, table, iframe, video, audio, svg, canvas";

/**
 * 한 블록 요소가 "AI 가 텍스트만 다듬어도 안전" 한지 판정.
 *  - 안전: p / h* / li / td / th / blockquote
 *  - 단, 이미지·표·iframe·video 등 구조적 자식이 있으면 텍스트만 바꾸면 그 자식이 사라지므로 제외
 *  - 텍스트가 비어있으면 (Tiptap 의 빈 placeholder 같은 경우) 제외
 *  - 서명 영역([data-signature] 자손) 은 AI 가 절대 건드리면 안 됨 — 사용자가 의도해서 박은 것
 */
function isRevisable(el) {
    if (!el || !el.tagName) return false;
    if (!SAFE_TAGS.has(el.tagName)) return false;
    if (el.querySelectorAll(STRUCTURAL_DESCENDANTS).length > 0) return false;
    if (el.closest && el.closest("[data-signature]")) return false;  // 서명 영역 제외
    const text = (el.textContent || "").trim();
    return text.length > 0;
}

/**
 * bodyHtml 에서 revisable 블록 요소들과 그 텍스트를 수집.
 *  반환: { doc, items: [{ el, text }, ...] }
 *  doc 은 cloneable 한 새 DOMDocument — applyRevisions 가 같은 doc 의 element 를 수정해 직렬화한다.
 */
export function extractBlocks(html) {
    const doc = new DOMParser().parseFromString(html || "", "text/html");
    // td/th 안에는 보통 <p> 가 들어있어서, p 를 우선시키고 td/th 단독으로는 추가 안 함 (중복 방지)
    const all = doc.body.querySelectorAll("p, h1, h2, h3, h4, h5, h6, li, blockquote, td, th");
    const items = [];
    const seen = new Set();
    all.forEach(el => {
        if (seen.has(el)) return;
        // td/th 가 자식 p 만 가진 경우, 자식 p 를 대표로 추가하고 td 자체는 건너뜀.
        // (Tiptap 표 구조: <td><p>cell text</p></td>)
        if ((el.tagName === "TD" || el.tagName === "TH")) {
            const childPs = el.querySelectorAll(":scope > p");
            if (childPs.length > 0) return; // 자식 p 가 별도로 처리됨
        }
        if (!isRevisable(el)) return;
        items.push({ el, text: (el.textContent || "").trim() });
        seen.add(el);
    });
    return { doc, items };
}

/** items → "[1] text\n[2] text..." 형식 직렬화 */
export function toNumbered(items) {
    return items.map((it, i) => `[${i + 1}] ${it.text}`).join("\n");
}

/**
 * AI 출력에서 [N] 별로 텍스트 추출. 한 번호 안에 여러 줄이 있을 수 있어 다음 [M] 만나기 전까지 누적.
 *  반환: Map<number, string>  (번호 → 다듬은 텍스트)
 */
export function parseNumbered(output) {
    const map = new Map();
    const lines = (output || "").split("\n");
    let curN = null;
    let curText = [];
    const flush = () => {
        if (curN !== null) {
            map.set(curN, curText.join("\n").trim());
        }
        curText = [];
    };
    for (const ln of lines) {
        const m = ln.match(/^\s*\[(\d+)\]\s?(.*)$/);
        if (m) {
            flush();
            curN = parseInt(m[1], 10);
            curText = [m[2] || ""];
        } else if (curN !== null) {
            curText.push(ln);
        }
    }
    flush();
    return map;
}

/**
 * [N] 파싱 실패 시 fallback — 빈 줄 단위로 단락 분리해 순서대로 1, 2, 3... 매핑.
 *  AI 가 시스템 프롬프트를 무시하고 [N] 형식 없이 응답한 경우에도 적용을 가능하게.
 */
export function parseNumberedWithFallback(output, expectedCount) {
    const primary = parseNumbered(output);
    if (primary.size > 0) return primary;

    // fallback — 단락 분리 (빈 줄 1개 이상으로 나눔). 그래도 부족하면 한 줄씩.
    let blocks = (output || "")
        .split(/\n\s*\n+/)
        .map(s => s.trim())
        .filter(Boolean);
    if (blocks.length < 2) {
        // 빈 줄 분리도 안 되면 한 줄씩
        blocks = (output || "")
            .split("\n")
            .map(s => s.trim())
            .filter(Boolean);
    }

    const map = new Map();
    blocks.forEach((b, i) => {
        if (i < expectedCount) map.set(i + 1, b);
    });
    return map;
}

/** 텍스트에서 선두의 "[N] " 표식만 제거. diff 화면 가독성 향상용. */
export function stripNumberPrefix(text) {
    return (text || "").replace(/^\s*\[\d+\]\s?/, "");
}

/**
 * AI 가 보낸 번호 매핑을 원본 doc 의 해당 element 에 적용.
 *  - el.textContent 로 교체 (안에 들어있던 인라인 마크업은 사라짐 — 이건 의도된 단순화).
 *  - 매핑 없는 번호는 그대로 둠.
 *  반환: doc.body.innerHTML 직렬화 결과
 */
export function applyRevisions(doc, items, map) {
    items.forEach((it, i) => {
        const revised = map.get(i + 1);
        if (typeof revised === "string") {
            it.el.textContent = revised;
        }
    });
    return doc.body.innerHTML;
}
